/**
 * Register Transaction Modal
 *
 * Modal for registering share transactions:
 * - Purchase/Sale (köp/försäljning)
 * - Gift (gåva)
 * - Inheritance (arv)
 * - New Issue (nyemission)
 */

import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowRightLeft,
  Gift,
  Heart,
  TrendingUp,
  AlertCircle,
  Info,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import {
  useShareClasses,
  useShareholdersSummary,
  useShareholderHoldings,
  useRegisterPurchaseTransaction,
  useRegisterGiftTransaction,
  useRegisterInheritanceTransaction,
  useRegisterNewIssueTransaction,
} from "@/hooks/use-share-book";
import type { ShareClass, ShareholderWithHoldings, ShareHolding } from "@/lib/api";

interface RegisterTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  shareBook: {
    hasPreemptionClause: boolean;
    hasConsentClause: boolean;
    hasRedemptionClause: boolean;
  };
}

type TransactionType = "purchase" | "gift" | "inheritance" | "new_issue";

const TRANSACTION_TYPES: {
  value: TransactionType;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  {
    value: "purchase",
    label: "Köp/Försäljning",
    description: "Överlåtelse av aktier mot betalning",
    icon: ArrowRightLeft,
  },
  {
    value: "gift",
    label: "Gåva",
    description: "Överlåtelse utan vederlag",
    icon: Gift,
  },
  {
    value: "inheritance",
    label: "Arv",
    description: "Överlåtelse via arvskifte",
    icon: Heart,
  },
  {
    value: "new_issue",
    label: "Nyemission",
    description: "Emission av nya aktier",
    icon: TrendingUp,
  },
];

const transactionSchema = z.object({
  transactionType: z.enum(["purchase", "gift", "inheritance", "new_issue"]),
  shareClassId: z.string().min(1, "Välj aktieslag"),
  fromShareholderId: z.string().optional(),
  toShareholderId: z.string().min(1, "Välj mottagare"),
  numberFrom: z.coerce.number().optional(),
  numberTo: z.coerce.number().optional(),
  quantity: z.coerce.number().optional(),
  pricePerShare: z.coerce.number().optional(),
  transactionDate: z.string().min(1, "Datum krävs"),
  registrationDate: z.string().optional(),
  notes: z.string().optional(),
  // Clause confirmations
  preemptionWaived: z.boolean().optional(),
  consentObtained: z.boolean().optional(),
  redemptionPeriodPassed: z.boolean().optional(),
}).refine((data) => {
  // For transfers, fromShareholderId and number range are required
  if (data.transactionType !== "new_issue") {
    if (!data.fromShareholderId) return false;
    if (!data.numberFrom || !data.numberTo) return false;
  }
  // For new_issue, quantity is required
  if (data.transactionType === "new_issue" && !data.quantity) {
    return false;
  }
  return true;
}, {
  message: "Fyll i alla obligatoriska fält",
}).refine((data) => {
  // Validate that numberFrom <= numberTo for transfers
  if (data.transactionType !== "new_issue") {
    if (data.numberFrom && data.numberTo && data.numberFrom > data.numberTo) {
      return false;
    }
  }
  return true;
}, {
  message: "Aktienummer 'från' måste vara mindre än eller lika med aktienummer 'till'",
  path: ["numberFrom"],
}).refine((data) => {
  // Validate that numberFrom is at least 1
  if (data.transactionType !== "new_issue" && data.numberFrom && data.numberFrom < 1) {
    return false;
  }
  return true;
}, {
  message: "Aktienummer måste vara minst 1",
  path: ["numberFrom"],
});

type TransactionFormData = z.infer<typeof transactionSchema>;

export function RegisterTransactionModal({
  isOpen,
  onClose,
  companyId,
  shareBook,
}: RegisterTransactionModalProps) {
  const [selectedType, setSelectedType] = useState<TransactionType>("purchase");
  const [selectedFromShareholder, setSelectedFromShareholder] = useState<string>("");

  const { data: shareClasses = [] } = useShareClasses(companyId);
  const { data: shareholders = [] } = useShareholdersSummary(companyId);
  const { data: fromHoldings = [] } = useShareholderHoldings(
    selectedFromShareholder || undefined
  );

  const registerPurchase = useRegisterPurchaseTransaction();
  const registerGift = useRegisterGiftTransaction();
  const registerInheritance = useRegisterInheritanceTransaction();
  const registerNewIssue = useRegisterNewIssueTransaction();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TransactionFormData>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      transactionType: "purchase",
      shareClassId: "",
      fromShareholderId: "",
      toShareholderId: "",
      numberFrom: undefined,
      numberTo: undefined,
      quantity: undefined,
      pricePerShare: undefined,
      transactionDate: new Date().toISOString().split("T")[0],
      registrationDate: "",
      notes: "",
      preemptionWaived: false,
      consentObtained: false,
      redemptionPeriodPassed: false,
    },
  });

  const watchShareClassId = watch("shareClassId");
  const watchFromShareholderId = watch("fromShareholderId");
  const watchNumberFrom = watch("numberFrom");
  const watchNumberTo = watch("numberTo");

  // Update selected from shareholder when form value changes
  useEffect(() => {
    if (watchFromShareholderId !== selectedFromShareholder) {
      setSelectedFromShareholder(watchFromShareholderId || "");
    }
  }, [watchFromShareholderId, selectedFromShareholder]);

  // Reset form when transaction type changes
  useEffect(() => {
    setValue("transactionType", selectedType);
    // Reset specific fields when changing type
    setValue("fromShareholderId", "");
    setValue("toShareholderId", "");
    setValue("numberFrom", undefined);
    setValue("numberTo", undefined);
    setValue("quantity", undefined);
    setValue("pricePerShare", undefined);
    setSelectedFromShareholder("");
  }, [selectedType, setValue]);

  // Get available holdings for the selected from shareholder and share class
  const availableHoldings = useMemo(() => {
    if (!watchShareClassId || !fromHoldings.length) return [];
    return fromHoldings.filter((h: ShareHolding) => h.shareClassId === watchShareClassId);
  }, [fromHoldings, watchShareClassId]);

  // Calculate quantity from number range
  const calculatedQuantity = useMemo(() => {
    if (!watchNumberFrom || !watchNumberTo) return 0;
    return Math.max(0, watchNumberTo - watchNumberFrom + 1);
  }, [watchNumberFrom, watchNumberTo]);

  // Active shareholders
  const activeShareholders = useMemo(() => {
    return shareholders.filter((s: ShareholderWithHoldings) => s.isActive);
  }, [shareholders]);

  // Get shareholders that can receive shares (exclude the "from" shareholder for transfers)
  const availableRecipients = useMemo(() => {
    if (selectedType === "new_issue") return activeShareholders;
    return activeShareholders.filter(
      (s: ShareholderWithHoldings) => s.id !== watchFromShareholderId
    );
  }, [activeShareholders, watchFromShareholderId, selectedType]);

  // Get shareholders that have holdings in the selected share class
  const shareholdersWithHoldings = useMemo(() => {
    if (!watchShareClassId) return [];
    return activeShareholders.filter((s: ShareholderWithHoldings) =>
      s.holdings.some((h) => h.shareClass.id === watchShareClassId && h.totalShares > 0)
    );
  }, [activeShareholders, watchShareClassId]);

  const onSubmit = async (data: TransactionFormData) => {
    try {
      if (data.transactionType === "new_issue") {
        await registerNewIssue.mutateAsync({
          companyId,
          data: {
            shareClassId: data.shareClassId,
            toShareholderId: data.toShareholderId,
            quantity: data.quantity || 0,
            pricePerShare: data.pricePerShare || 0,
            transactionDate: data.transactionDate,
            registrationDate: data.registrationDate || undefined,
            notes: data.notes,
          },
        });
        toast.success("Nyemission registrerad");
      } else {
        const commonData = {
          shareClassId: data.shareClassId,
          fromShareholderId: data.fromShareholderId!,
          toShareholderId: data.toShareholderId,
          numberFrom: data.numberFrom!,
          numberTo: data.numberTo!,
          transactionDate: data.transactionDate,
          notes: data.notes,
        };

        switch (data.transactionType) {
          case "purchase":
            await registerPurchase.mutateAsync({
              companyId,
              data: {
                ...commonData,
                pricePerShare: data.pricePerShare || 0,
                preemptionWaived: data.preemptionWaived,
                consentObtained: data.consentObtained,
              },
            });
            toast.success("Köp registrerat");
            break;

          case "gift":
            await registerGift.mutateAsync({
              companyId,
              data: {
                ...commonData,
                preemptionWaived: data.preemptionWaived,
                consentObtained: data.consentObtained,
              },
            });
            toast.success("Gåva registrerad");
            break;

          case "inheritance":
            await registerInheritance.mutateAsync({
              companyId,
              data: commonData,
            });
            toast.success("Arv registrerat");
            break;
        }
      }

      handleClose();
    } catch (error) {
      console.error("Transaction error:", error);
      // Show error message to user
      const errorMessage = error instanceof Error
        ? error.message
        : "Ett fel uppstod vid registrering av transaktionen";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedType("purchase");
    setSelectedFromShareholder("");
    onClose();
  };

  // Check if clauses need to be confirmed
  const needsClauseConfirmation =
    selectedType !== "new_issue" &&
    selectedType !== "inheritance" &&
    (shareBook.hasPreemptionClause ||
      shareBook.hasConsentClause ||
      shareBook.hasRedemptionClause);

  const isTransfer = selectedType !== "new_issue";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Registrera transaktion"
      size="lg"
    >
      <div className="space-y-6">
        {/* Transaction Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Typ av transaktion
          </label>
          <div className="grid grid-cols-2 gap-3">
            {TRANSACTION_TYPES.map((type) => {
              const Icon = type.icon;
              const isSelected = selectedType === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setSelectedType(type.value)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        isSelected ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <Icon size={20} />
                    </div>
                    <div>
                      <p
                        className={`font-medium ${
                          isSelected ? "text-blue-900" : "text-gray-900"
                        }`}
                      >
                        {type.label}
                      </p>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Share Class */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aktieslag *
            </label>
            <select {...register("shareClassId")} className="input">
              <option value="">Välj aktieslag</option>
              {shareClasses.map((sc: ShareClass) => (
                <option key={sc.id} value={sc.id}>
                  {sc.name} ({sc.code}) - {sc.totalShares.toLocaleString("sv-SE")} aktier
                </option>
              ))}
            </select>
            {errors.shareClassId && (
              <p className="text-sm text-red-600 mt-1">{errors.shareClassId.message}</p>
            )}
          </div>

          {/* Transfer-specific fields */}
          {isTransfer && (
            <>
              {/* From Shareholder */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {selectedType === "inheritance" ? "Dödsbo/Avliden" : "Säljare/Överlåtare"} *
                </label>
                <select
                  {...register("fromShareholderId")}
                  className="input"
                >
                  <option value="">Välj aktieägare</option>
                  {shareholdersWithHoldings.map((s: ShareholderWithHoldings) => {
                    const holding = s.holdings.find(
                      (h) => h.shareClass.id === watchShareClassId
                    );
                    return (
                      <option key={s.id} value={s.id}>
                        {s.name} ({holding?.totalShares.toLocaleString("sv-SE") || 0} aktier)
                      </option>
                    );
                  })}
                </select>
                {errors.fromShareholderId && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.fromShareholderId.message}
                  </p>
                )}
              </div>

              {/* Available Holdings Info */}
              {watchFromShareholderId && availableHoldings.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">Tillgängliga aktier:</p>
                      <ul className="mt-1 space-y-1">
                        {availableHoldings.map((h: ShareHolding, idx: number) => (
                          <li key={idx} className="text-blue-700">
                            Nr {h.numberFrom} - {h.numberTo} ({h.numberTo - h.numberFrom + 1} st)
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Share Number Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aktienummer från *
                  </label>
                  <input
                    type="number"
                    {...register("numberFrom")}
                    className="input"
                    min={1}
                  />
                  {errors.numberFrom && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.numberFrom.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aktienummer till *
                  </label>
                  <input
                    type="number"
                    {...register("numberTo")}
                    className="input"
                    min={1}
                  />
                  {errors.numberTo && (
                    <p className="text-sm text-red-600 mt-1">
                      {errors.numberTo.message}
                    </p>
                  )}
                </div>
              </div>

              {calculatedQuantity > 0 && (
                <p className="text-sm text-gray-600">
                  Antal aktier: <span className="font-medium">{calculatedQuantity.toLocaleString("sv-SE")}</span>
                </p>
              )}
            </>
          )}

          {/* New Issue specific fields */}
          {!isTransfer && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Antal nya aktier *
              </label>
              <input
                type="number"
                {...register("quantity")}
                className="input"
                min={1}
              />
              {errors.quantity && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.quantity.message}
                </p>
              )}
            </div>
          )}

          {/* To Shareholder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {selectedType === "new_issue"
                ? "Tecknare"
                : selectedType === "inheritance"
                ? "Arvinge"
                : "Köpare/Mottagare"}{" "}
              *
            </label>
            <select {...register("toShareholderId")} className="input">
              <option value="">Välj aktieägare</option>
              {availableRecipients.map((s: ShareholderWithHoldings) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors.toShareholderId && (
              <p className="text-sm text-red-600 mt-1">
                {errors.toShareholderId.message}
              </p>
            )}
            {availableRecipients.length === 0 && watchShareClassId && (
              <p className="text-sm text-amber-600 mt-1">
                Inga aktieägare tillgängliga. Lägg till en aktieägare först.
              </p>
            )}
          </div>

          {/* Price per Share (for purchase and new_issue) */}
          {(selectedType === "purchase" || selectedType === "new_issue") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pris per aktie (kr) {selectedType === "new_issue" ? "*" : ""}
              </label>
              <input
                type="number"
                step="0.01"
                {...register("pricePerShare")}
                className="input"
                min={0}
              />
              {errors.pricePerShare && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.pricePerShare.message}
                </p>
              )}
            </div>
          )}

          {/* Transaction Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              Transaktionsdatum *
            </label>
            <input
              type="date"
              {...register("transactionDate")}
              className="input"
            />
            {errors.transactionDate && (
              <p className="text-sm text-red-600 mt-1">{errors.transactionDate.message}</p>
            )}
          </div>

          {/* Registration Date (for new_issue) */}
          {selectedType === "new_issue" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registreringsdatum hos Bolagsverket
              </label>
              <input
                type="date"
                {...register("registrationDate")}
                className="input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Valfritt - datum då nyemissionen registrerades hos Bolagsverket
              </p>
            </div>
          )}

          {/* Clause Confirmations */}
          {needsClauseConfirmation && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-amber-600 mt-0.5 flex-shrink-0" size={18} />
                <div>
                  <p className="font-medium text-amber-900">Förbehåll i bolagsordningen</p>
                  <p className="text-sm text-amber-700">
                    Bekräfta att nedanstående villkor är uppfyllda för överlåtelsen.
                  </p>
                </div>
              </div>

              <div className="space-y-2 ml-6">
                {shareBook.hasPreemptionClause && (
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      {...register("preemptionWaived")}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Förköpsförbehåll:</span> Förköpsrätten har erbjudits och
                      antingen utövats eller avståtts av berättigade aktieägare.
                    </span>
                  </label>
                )}

                {shareBook.hasConsentClause && (
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      {...register("consentObtained")}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Samtyckesförbehåll:</span> Styrelsens samtycke har
                      erhållits för överlåtelsen.
                    </span>
                  </label>
                )}

                {shareBook.hasRedemptionClause && (
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      {...register("redemptionPeriodPassed")}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">Hembudsförbehåll:</span> Hembudet har genomförts och
                      lösningsperioden har passerat eller lösningsrätten har avståtts.
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anteckningar
            </label>
            <textarea
              {...register("notes")}
              className="input min-h-[80px]"
              placeholder="Valfria anteckningar om transaktionen..."
            />
          </div>

          {/* Info box */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              <strong>Viktigt:</strong> Transaktioner kan inte ändras efter registrering
              enligt ABL 5 kap. Vid felaktig registrering, använd en korrigeringstransaktion.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Registrerar...
                </>
              ) : (
                "Registrera transaktion"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
